import type { ComponentPropsWithoutRef, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'secondary' | 'ghost'
type ButtonSize = 'small' | 'large'

type BaseProps = {
    children: ReactNode
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
}

type LinkButtonProps = BaseProps & {
    href: string
} & Omit<ComponentPropsWithoutRef<'a'>, 'href' | 'className' | 'children'>

type ActionButtonProps = BaseProps & Omit<ComponentPropsWithoutRef<'button'>, 'className' | 'children'>

const BASE = 'group/button relative inline-flex select-none items-center justify-center gap-2.5 rounded-xl font-medium tracking-[-0.02em] leading-6 whitespace-nowrap transition-[transform,background-color,box-shadow,color] duration-300 ease-[cubic-bezier(0.44,0,0.56,1)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70 active:translate-y-0 disabled:pointer-events-none disabled:opacity-50'

const SIZES: Record<ButtonSize, string> = {
    small: 'px-[13px] py-[9px] text-[15px]',
    large: 'px-[18px] py-3 text-base'
}

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'bg-[#8b5cf6] text-white shadow-[0_0_0_1px_rgba(139,92,246,0.12)] backdrop-blur-[10px] hover:-translate-y-0.5 hover:bg-[#a276ff] hover:shadow-[0_10px_30px_-10px_rgba(139,92,246,0.7),0_0_0_1px_rgba(139,92,246,0.24)]',
    secondary: 'bg-surface-0 text-[#fcfcfa] shadow-[0_0_0_1px_rgba(255,255,255,0.08)] backdrop-blur-[10px] hover:-translate-y-0.5 hover:bg-surface-5 hover:shadow-[0_10px_30px_-12px_rgba(0,0,0,0.9),0_0_0_1px_rgba(255,255,255,0.16)]',
    ghost: 'text-white/56 hover:text-white'
}

export const Button = ({
    children,
    href,
    variant = 'primary',
    size = 'large',
    className,
    ...rest
}: LinkButtonProps) => {
    const classes = cn(BASE, SIZES[size], VARIANTS[variant], className)
    const isInternal = href.startsWith('/') || href.startsWith('#')

    if (!isInternal) {
        return (
            <a
                href={href}
                className={classes}
                {...rest}
            >
                {children}
            </a>
        )
    }

    return (
        <Link
            href={href}
            className={classes}
            {...rest}
        >
            {children}
        </Link>
    )
}

export const ActionButton = ({
    children,
    variant = 'primary',
    size = 'large',
    className,
    type = 'button',
    ...rest
}: ActionButtonProps) => {
    return (
        <button
            type={type}
            className={cn(BASE, SIZES[size], VARIANTS[variant], className)}
            {...rest}
        >
            {children}
        </button>
    )
}
